import { useState } from 'react';

import { useGetCustomerData } from '@/Application/Hooks/GetCustomerData';
import type { Address } from '@/modules/address/domain/Address';
import { getSessionData } from '@/modules/shared/utils/getSessionData';
import { SendShippingEvents } from '@/modules/shared/utils/gtmEvents';
import {
  ShippingEventInteractiveActions,
  ShippingEventInteractiveTags
} from '@/modules/shared/utils/gtmEvents/types';
import { MODALS } from '@/Pages/ShippingPage/ShippingPage';
import { useShippingPageContext } from '@/Pages/ShippingPage/ShippingPageContext';
import AddressCard from '@/sections/address/components/AddressCard/AddressCard';
import AddressFormModal from '@/sections/address/components/AddressFormModal/AddressFormModal';
import type { AddressMainProps } from '@/sections/address/components/AddressMain/interfaces';
import SelectionAddressModal from '@/sections/address/components/SelectionAddressModal/SelectionAddressModal';
import ShippingOptionsUpdatedModal from '@/sections/address/components/ShippingOptionsUpdatedModal/ShippingOptionsUpdatedModal';
import useCreateShippingAddress from '@/sections/address/hooks/mutators/useCreateShippingAddress/useCreateShippingAddress';
import { useLabeledAddress } from '@/sections/address/hooks/useLabeledAddress';
import { useCustomerContext } from '@/sections/customer/CustomerContext';
import useCreateCustomerAddress from '@/sections/customer/hooks/useSaveAddress';
import useUpdateCustomerAddress from '@/sections/customer/hooks/useUpdateCustomerAddress';
import { usePlaceData } from '@/sections/region/GooglePlaceDataContext';
import useGetGeneralShippingMethods from '@/sections/shippingMethods/hooks/useGetGeneralShippingMethods';

const AddressMain: React.FC<AddressMainProps> = ({
  modalOpen,
  defaultAddress,
  handleSetModals,
  hasAddresses,
  showAlert,
  handleSetOptions
}) => {
  const { completeSkuHistory } = useShippingPageContext();
  const [addressToEdit, setAddressToEdit] = useState<Address | undefined>(
    undefined
  );
  const [isShippingOptionsModalOpen, setIsShippingOptionsModalOpen] =
    useState(false);
  const [isClosingAfterSave, setIsClosingAfterSave] = useState(false);

  const {
    trigger: createShippingAddress,
    isLoading: isLoadingShippingAddress
  } = useCreateShippingAddress();
  const {
    trigger: createCustomerAddress,
    isLoading: isLoadingCreateCustomerAddress
  } = useCreateCustomerAddress();
  const {
    trigger: updateCustomerAddress,
    isLoading: isLoadingUpdateCustomerAddress
  } = useUpdateCustomerAddress();
  const {
    customer: { data: customer, isLoading }
  } = useCustomerContext();
  const { refetchShippingMethods, isLoading: isLoadingShippingMethods } =
    useGetGeneralShippingMethods();
  const addressLabel = useLabeledAddress(defaultAddress);
  const { anonymousId, email, customerRut, customerId } = useGetCustomerData();
  const { setPlaceData } = usePlaceData();

  const handleAddressSaved = () => {
    setIsClosingAfterSave(true);
    setIsShippingOptionsModalOpen(true);
    handleSetModals(null);
    setPlaceData(null);

    setTimeout(() => {
      setIsClosingAfterSave(false);
    }, 100);
  };

  if (isLoading) return null;

  const isLoadingNewAddress =
    isLoadingShippingAddress ||
    isLoadingCreateCustomerAddress ||
    isLoadingUpdateCustomerAddress ||
    isLoadingShippingMethods ||
    isClosingAfterSave;

  return (
    <>
      <AddressCard
        onAddAddress={() => {
          handleSetModals(MODALS.CREATE_ADDRESS);
          SendShippingEvents.sendEvent({
            event: 'baseEvent',
            extraFields: {
              'event-interactive-tag': ShippingEventInteractiveTags.AddAddress
            }
          });
        }}
        onEditAddress={() => {
          if (anonymousId) handleSetModals(MODALS.CREATE_ADDRESS);
          if (customerId) handleSetModals(MODALS.EDIT_ADDRESS);

          SendShippingEvents.sendEvent({
            event: 'baseEvent',
            extraFields: {
              'event-interactive-tag':
                ShippingEventInteractiveTags.ModifyAddress
            }
          });
        }}
        addressLabel={addressLabel}
        hasAddresses={hasAddresses}
      />
      <SelectionAddressModal
        isOpen={modalOpen === MODALS.EDIT_ADDRESS}
        onClose={() => handleSetModals(null)}
        onCreateNewAddress={() => {
          setAddressToEdit(undefined);
          handleSetModals(MODALS.CREATE_ADDRESS);
        }}
        onEditAddress={(address) => {
          SendShippingEvents.sendEvent({
            event: 'baseEvent',
            extraFields: {
              'event-interactive-accion':
                ShippingEventInteractiveActions.ModalMyAddress,
              'event-interactive-tag': ShippingEventInteractiveTags.PencilIcon
            }
          });
          setAddressToEdit({
            ...address
          });
          handleSetModals(MODALS.CREATE_ADDRESS);
        }}
        handleSetOptions={handleSetOptions}
        onShippingOptionsModalOpen={() => setIsShippingOptionsModalOpen(true)}
      />
      {modalOpen === MODALS.CREATE_ADDRESS && (
        <AddressFormModal
          onSaved={handleAddressSaved}
          showAlert={showAlert}
          isOpen={modalOpen === MODALS.CREATE_ADDRESS}
          onClose={() => {
            handleSetModals(null);
            setPlaceData(null);
          }}
          onSubmit={async (addressPayload, saveAddress) => {
            if (anonymousId) {
              addressPayload.email = email;
              addressPayload.customerRut = customerRut;
              const streetNameUntilFirstComma =
                addressPayload?.streetName?.split(',')?.[0];
              addressPayload.alias = streetNameUntilFirstComma || '';
            }
            await createShippingAddress(addressPayload);
            const sessionData = getSessionData();
            const address = addressPayload as Address;
            if (sessionData && saveAddress) {
              if (address?.id) {
                await updateCustomerAddress(address);
              } else {
                await createCustomerAddress(address);
                await refetchShippingMethods(completeSkuHistory);
              }
            } else {
              await refetchShippingMethods(completeSkuHistory);
            }
            handleSetOptions();
            setAddressToEdit(undefined);
            handleSetModals(address?.id ? MODALS.EDIT_ADDRESS : null);
          }}
          initialValues={
            addressToEdit ?? (customer ? undefined : defaultAddress)
          }
          isLoadingNewAddress={isLoadingNewAddress}
        />
      )}
      <ShippingOptionsUpdatedModal
        isOpen={isShippingOptionsModalOpen}
        onClose={() => setIsShippingOptionsModalOpen(false)}
      />
    </>
  );
};
export default AddressMain;
